import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabsModule } from 'primeng/tabs';
import { MultiFormAccordionComponent } from '@/components/multi-form-accordion/multi-form-accordion.component';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { DocType } from '@/types';
import { combineLatest, map, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MultiFormAccordionProps } from '@/components/multi-form-accordion/multiform-accordion.types';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { DynamicForm, FieldVariant } from '@/components/form-builder/form-builder.types';
import { DacSubmissionPiecesComponent } from '../../dac-submission-pieces/dac-submission-pieces.component';
import { DacDocSubmissionRepositoryFactory } from '@/store/dac-doc-submission/dac-doc-submission-state.repository';
import { DacDocSubmissionStateProps } from '@/store/dac-doc-submission/dac-doc-submission-state.store';
import { DacDocSubmissionDto } from '@/types';
import { ElfBaseStore } from '@/store/base-store.repository';
import { startWith } from 'rxjs/operators';

export interface DacSubmissionPiecesTabsProps {
  docTypes: DocType[];
  accordions: MultiFormAccordionService[];
  dacId?: number;
  dacVer?: number;
  isDisabled?: Observable<boolean>;
  translate: (key: string, params?: Record<string, any>) => string;
}

@Component({
  selector: 'sm-dac-submission-pieces-tabs',
  standalone: true,
  imports: [CommonModule, TabsModule, TranslateModule, FormBuilderComponent],
  template: `
    <p-tabs [(value)]="selectedTab" scrollable class="bg-transparent mb-4 w-full">
      <p-tablist>
        <p-tab *ngFor="let item of tabsData; let i = index" [value]="i.toString()">
          {{ item.title }}
        </p-tab>
      </p-tablist>
      <p-tabpanels>
        <p-tabpanel *ngFor="let item of tabsData; let i = index" [value]="i.toString()">
          <sm-form-builder *ngIf="item.formObject" [formObject]="item.formObject"></sm-form-builder>
        </p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  `,
})
export class DacSubmissionPiecesTabsComponent implements OnInit {
  @Input() docTypes!: DocType[];
  @Input() accordions!: MultiFormAccordionService[];
  @Input() dacId?: number;
  @Input() dacVer?: number;
  @Input() isDisabled?: Observable<boolean>;
  @Input() translate!: (key: string, params?: Record<string, any>) => string;

  selectedTab = '0';
  tabsData: { title: string; formObject: DynamicForm }[] = [];

  ngOnInit(): void {
    this.tabsData = this.docTypes.map((docType, index) => {
      // Create observable to track used submission piece IDs across other accordions
      const usedDocSubmissionIds$ = this.createUsedDocSubmissionIdsObservable(index);

      const accordionObject: MultiFormAccordionProps = {
        titles: [this.translate('dac.form.submissionPiece2', { type: docType.libelle.toLowerCase() })],
        component: DacSubmissionPiecesComponent,
        injectKeys: ['dacId', 'dacVer', 'docTypeId', 'isDisabled', 'usedDocSubmissionIds', 'accordion'],
        dacId: this.dacId,
        dacVer: this.dacVer,
        isDisabled: this.isDisabled,
        docTypeId: docType.id,
        usedDocSubmissionIds: usedDocSubmissionIds$,
        accordion: this.accordions[index],
        customFactoryMultiFormAccordion: this.accordions[index],
      };

      const title = this.translate('dac.form.submissionPiece', { type: docType.libelle.toLowerCase() });

      const formObject: DynamicForm = {
        title: '',
        isHeaderHidden: true,
        grids: [
          {
            title: '',
            isHeaderHidden: true,
            gridItems: [
              {
                fields: [
                  {
                    variant: FieldVariant.CUSTOM,
                    props: {
                      component: MultiFormAccordionComponent,
                      injectKeys: ['accordionObject'],
                      accordionObject,
                    },
                  },
                ],
              },
            ],
          },
        ],
      };

      return { title, formObject };
    });
  }

  private createUsedDocSubmissionIdsObservable(currentTabIndex: number): Observable<number[]> {
    // Create an observable that combines all submission pieces from all accordions
    const storeObservables = this.accordions.map((accordion, accordionIndex) => {
      // Get all stores for this accordion and track their changes
      return this.getAllStoresObservable(accordion).pipe(
        map(stores => {
          // Extract docSoumissionId from all DTOs in these stores
          const usedIds = stores
            .map(store => {
              const repo = DacDocSubmissionRepositoryFactory.create(store);
              return repo.get<DacDocSubmissionDto>('dto')?.docSoumissionId;
            })
            .filter((id): id is number => id !== undefined && id !== null);

          return { accordionIndex, usedIds };
        })
      );
    });

    return combineLatest(storeObservables).pipe(
      map(results => {
        // Collect all used IDs, but exclude ones from the current accordion
        const allUsedIds: number[] = [];
        results.forEach(({ accordionIndex, usedIds }) => {
          if (accordionIndex !== currentTabIndex) {
            allUsedIds.push(...usedIds);
          }
        });
        return allUsedIds;
      }),
      startWith([])
    );
  }

  private getAllStoresObservable(accordion: MultiFormAccordionService): Observable<ElfBaseStore<DacDocSubmissionStateProps>[]> {
    return new Observable<ElfBaseStore<DacDocSubmissionStateProps>[]>(observer => {
      // Emit current stores
      observer.next(accordion.getAllStores());

      // Subscribe to new stores being added
      const newStoreSub = accordion.newStore$.subscribe(() => {
        observer.next(accordion.getAllStores());
      });

      // Subscribe to stores being removed
      const removeStoreSub = accordion.storeRemoved$.subscribe(() => {
        observer.next(accordion.getAllStores());
      });

      return () => {
        newStoreSub.unsubscribe();
        removeStoreSub.unsubscribe();
      };
    }).pipe(startWith([] as ElfBaseStore<DacDocSubmissionStateProps>[]));
  }
}
